import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, Timeline, Collapse, Button, Space, Spin, Tooltip } from 'antd';
import {
  HistoryOutlined,
  ReloadOutlined,
  BugOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { bugReportApi, type BugFixLog } from '../../../services/bugReportApi';
import type { BugReport } from '../../../components/BugReport/types';

const { Text } = Typography;

interface ModuleSummary {
  key: string;
  formId: string | null;
  page: string;
  bugCount: number;
  openCount: number;
  resolvedCount: number;
  lastActivity: string;
}

const ACTION_COLORS: Record<string, string> = {
  diagnosed: 'blue',
  fix_applied: 'green',
  fix_verified: 'cyan',
  fix_failed: 'red',
  reopened: 'orange',
  rebuild_needed: 'purple',
};

const ACTION_LABELS: Record<string, string> = {
  diagnosed: 'Diagnosed',
  fix_applied: 'Fix Applied',
  fix_verified: 'Fix Verified',
  fix_failed: 'Fix Failed',
  reopened: 'Reopened',
  rebuild_needed: 'Rebuild Needed',
};

const RESULT_COLORS: Record<string, string> = {
  success: 'green',
  partial: 'orange',
  failed: 'red',
  pending: 'blue',
  skipped: 'default',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'red',
  in_progress: 'orange',
  resolved: 'green',
  closed: 'default',
};

const BugFixHistoryPanel: React.FC = () => {
  const [modules, setModules] = useState<ModuleSummary[]>([]);
  const [bugsMap, setBugsMap] = useState<Record<string, BugReport[]>>({});
  const [logsMap, setLogsMap] = useState<Record<string, BugFixLog[]>>({});
  const [loadingLogs, setLoadingLogs] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const fetchAllBugs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bugReportApi.list({ size: 200 });
      const bugs = res.data || [];

      // Group by formId (preferred) or page path
      const grouped: Record<string, BugReport[]> = {};
      for (const bug of bugs) {
        const key = bug.formId || bug.page.replace(/^\//, '') || 'unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(bug);
      }

      const summaries: ModuleSummary[] = Object.entries(grouped).map(([key, groupBugs]) => {
        const sorted = [...groupBugs].sort(
          (a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        );
        return {
          key,
          formId: groupBugs[0].formId,
          page: groupBugs[0].page,
          bugCount: groupBugs.length,
          openCount: groupBugs.filter(b => b.status === 'open' || b.status === 'in_progress').length,
          resolvedCount: groupBugs.filter(b => b.status === 'resolved' || b.status === 'closed').length,
          lastActivity: sorted[0]?.updatedAt || sorted[0]?.createdAt || '',
        };
      });

      // Sort by open count desc, then by last activity desc
      summaries.sort((a, b) => {
        if (b.openCount !== a.openCount) return b.openCount - a.openCount;
        return new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime();
      });

      setModules(summaries);
      setBugsMap(grouped);
    } catch (err) {
      console.error('Failed to fetch bugs for history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllBugs();
  }, [fetchAllBugs]);

  const fetchLogsForModule = async (moduleKey: string, formId: string | null) => {
    if (logsMap[moduleKey]) return; // already fetched
    setLoadingLogs(prev => ({ ...prev, [moduleKey]: true }));
    try {
      if (formId) {
        const logs = await bugReportApi.getModuleBugHistory(formId);
        setLogsMap(prev => ({ ...prev, [moduleKey]: logs }));
      } else {
        // Fallback: fetch by pagePath
        const res = await bugReportApi.listFixLogs({ pagePath: moduleKey, size: 100 });
        setLogsMap(prev => ({ ...prev, [moduleKey]: res.data || [] }));
      }
    } catch (err) {
      console.error('Failed to fetch fix logs for', moduleKey, err);
      setLogsMap(prev => ({ ...prev, [moduleKey]: [] }));
    } finally {
      setLoadingLogs(prev => ({ ...prev, [moduleKey]: false }));
    }
  };

  const renderTimeline = (moduleKey: string) => {
    const bugs = bugsMap[moduleKey] || [];
    const logs = logsMap[moduleKey] || [];
    const isLoading = loadingLogs[moduleKey];

    if (isLoading) {
      return <Spin size="small" style={{ display: 'block', padding: 16, textAlign: 'center' }} />;
    }

    // Build combined chronological entries
    type TimelineEntry = {
      type: 'bug' | 'log';
      date: string;
      data: BugReport | BugFixLog;
    };

    const entries: TimelineEntry[] = [];

    for (const bug of bugs) {
      entries.push({ type: 'bug', date: bug.createdAt, data: bug });
    }
    for (const log of logs) {
      entries.push({ type: 'log', date: log.createdAt, data: log });
    }

    // Sort chronologically (oldest first)
    entries.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (entries.length === 0) {
      return <Text type="secondary">No history records found.</Text>;
    }

    return (
      <Timeline
        style={{ marginTop: 8 }}
        items={entries.map((entry) => {
          if (entry.type === 'bug') {
            const bug = entry.data as BugReport;
            const annotations = bug.annotations || [];
            const screenshots = bug.screenshots || [];
            return {
              key: `bug-${bug.id}`,
              color: STATUS_COLORS[bug.status] || 'gray',
              dot: <BugOutlined />,
              children: (
                <div>
                  <Space size="small" style={{ marginBottom: 4 }}>
                    <Text strong>Bug #{bug.id}</Text>
                    <Tag color={STATUS_COLORS[bug.status] || 'default'}>{bug.status}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      by {bug.reportedBy}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {new Date(bug.createdAt).toLocaleString('zh-TW')}
                    </Text>
                  </Space>
                  {annotations.length > 0 && (
                    <Collapse
                      size="small"
                      style={{ marginTop: 4 }}
                      items={annotations.map((a, i) => ({
                        key: i,
                        label: (
                          <span>
                            <Tag color="red" style={{ fontSize: 10 }}>#{i + 1}</Tag>
                            {a.label && <Tag color="blue" style={{ fontSize: 10 }}>{a.label}</Tag>}
                            <Text style={{ fontSize: 12 }}>{a.description?.substring(0, 60)}{a.description?.length > 60 ? '...' : ''}</Text>
                          </span>
                        ),
                        children: (
                          <div>
                            <p style={{ margin: '4px 0' }}>{a.description}</p>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              Element: &lt;{a.elementTag}&gt; | Text: &quot;{a.elementText}&quot;
                            </Text>
                          </div>
                        ),
                      }))}
                    />
                  )}
                  {screenshots.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                      <FileTextOutlined /> {screenshots.length} screenshot(s) attached
                    </Text>
                  )}
                  {bug.resolution && (
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 12 }}>Resolution: {bug.resolution}</Text>
                    </div>
                  )}
                </div>
              ),
            };
          } else {
            const log = entry.data as BugFixLog;
            const actionColor = ACTION_COLORS[log.action] || 'default';
            const resultColor = RESULT_COLORS[log.result] || 'default';
            const dotIcon = log.result === 'success' ? <CheckCircleOutlined /> :
              log.result === 'failed' ? <CloseCircleOutlined /> :
              log.result === 'partial' ? <ExclamationCircleOutlined /> :
              <ClockCircleOutlined />;
            return {
              key: `log-${log.id}`,
              color: actionColor,
              dot: dotIcon,
              children: (
                <div>
                  <Space size="small" style={{ marginBottom: 4 }}>
                    <Tag color={actionColor}>{ACTION_LABELS[log.action] || log.action}</Tag>
                    <Tag color={resultColor}>{log.result}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Bug #{log.bugId}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {log.actor && `by ${log.actor}`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {new Date(log.createdAt).toLocaleString('zh-TW')}
                    </Text>
                  </Space>
                  {log.diagnosis && (
                    <div style={{ marginTop: 2 }}>
                      <Text style={{ fontSize: 12 }}><Text strong style={{ fontSize: 12 }}>Diagnosis:</Text> {log.diagnosis}</Text>
                    </div>
                  )}
                  {log.rootCause && (
                    <div style={{ marginTop: 2 }}>
                      <Text style={{ fontSize: 12 }}><Text strong style={{ fontSize: 12 }}>Root Cause:</Text> {log.rootCause}</Text>
                    </div>
                  )}
                  {log.filesChanged && log.filesChanged.length > 0 && (
                    <Collapse
                      size="small"
                      style={{ marginTop: 4 }}
                      items={[{
                        key: 'files',
                        label: <Text style={{ fontSize: 12 }}>Files Changed ({log.filesChanged.length})</Text>,
                        children: (
                          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
                            {log.filesChanged.map((f, i) => (
                              <li key={i}>
                                <code style={{ fontSize: 11 }}>{f.path}</code>
                                {f.desc && <Text type="secondary" style={{ fontSize: 11 }}> - {f.desc}</Text>}
                              </li>
                            ))}
                          </ul>
                        ),
                      }]}
                    />
                  )}
                  {log.notes && (
                    <div style={{ marginTop: 2 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Notes: {log.notes}</Text>
                    </div>
                  )}
                </div>
              ),
            };
          }
        })}
      />
    );
  };

  const columns: ColumnsType<ModuleSummary> = [
    {
      title: 'Module',
      key: 'module',
      width: 180,
      render: (_: unknown, record: ModuleSummary) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.formId || record.key}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.page}</Text>
        </Space>
      ),
    },
    {
      title: 'Bugs',
      dataIndex: 'bugCount',
      key: 'bugCount',
      width: 80,
      align: 'center',
      render: (count: number) => <Tag>{count}</Tag>,
    },
    {
      title: 'Open',
      dataIndex: 'openCount',
      key: 'openCount',
      width: 80,
      align: 'center',
      render: (count: number) =>
        count > 0 ? <Tag color="red">{count}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: 'Resolved',
      dataIndex: 'resolvedCount',
      key: 'resolvedCount',
      width: 80,
      align: 'center',
      render: (count: number) =>
        count > 0 ? <Tag color="green">{count}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      width: 160,
      render: (val: string) => val ? new Date(val).toLocaleString('zh-TW') : '--',
    },
  ];

  return (
    <Card
      title={
        <Space>
          <HistoryOutlined style={{ color: '#1677ff' }} />
          <span>Bug Fix History by Module</span>
        </Space>
      }
      extra={
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={fetchAllBugs} loading={loading}>
            Refresh
          </Button>
        </Tooltip>
      }
    >
      <Table
        columns={columns}
        dataSource={modules}
        rowKey="key"
        loading={loading}
        size="small"
        pagination={{
          pageSize: 15,
          showSizeChanger: false,
          showTotal: t => `${t} modules`,
        }}
        expandable={{
          expandedRowRender: (record) => renderTimeline(record.key),
          onExpand: (expanded, record) => {
            if (expanded) {
              fetchLogsForModule(record.key, record.formId);
            }
          },
        }}
      />
    </Card>
  );
};

export default BugFixHistoryPanel;
